import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../../config";
import "../../styles/Schedule.css";

// Pick the hours to book on one screen.
//
// --- whose clock is this? -----------------------------------------------
//
// The screen's. An advertiser buys the shop's 9am, not their own -- an ad on a
// Stroudsburg window at "9am" means nine in the morning on that street,
// whatever timezone the buyer is sitting in. Every hour here is labelled in
// the screen's local time and the panel says so out loud, because a buyer in
// California silently getting 6am local would be a refund and a bad review.
//
// The API returns both halves: `hour` is the local hour to show, `start_utc`
// is the instant it actually is. Only start_utc is ever sent back.
//
// --- why a grid and not two dropdowns -----------------------------------
//
// A start and end pair cannot express "that afternoon is already sold". The
// grid shows booked, closed and past hours as what they are, so the reason a
// selection is refused is visible before it is attempted rather than as a 409
// afterwards.

const STATUS_LABEL = {
    available: "Available",
    booked: "Already booked",
    closed: "Screen is off air",
    past: "In the past",
    unbookable: "Not bookable yet",
};

function money(cents) {
    if (cents === null || cents === undefined) return "—";
    return `$${(cents / 100).toFixed(2)}`;
}

export default function HourPicker({ deviceId, onChange }) {
    const [data, setData] = useState(null);
    const [dayIndex, setDayIndex] = useState(0);
    const [selected, setSelected] = useState([]);   // start_utc strings
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setError("");
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/devices/${deviceId}/availability?days=7`
            );
            if (!res.ok) {
                setError("Could not load this screen's availability.");
                return;
            }
            setData(await res.json());
        } catch {
            setError("Network error loading availability.");
        }
    }, [deviceId]);

    useEffect(() => { load(); }, [load]);

    const day = data && data.days ? data.days[dayIndex] : null;

    // A booking is one contiguous run of hours. Clicking outside the current
    // run starts a new one rather than creating a gap: a single booking with a
    // hole in it is not a thing the server can express, and silently splitting
    // it into two charges would be a surprise at checkout.
    function toggle(hour) {
        if (hour.status !== "available") return;
        setSelected((prev) => {
            if (prev.includes(hour.start_utc)) {
                // Only the ends can be unpicked, or the run would break.
                if (hour.start_utc === prev[0]) return prev.slice(1);
                if (hour.start_utc === prev[prev.length - 1]) return prev.slice(0, -1);
                return prev;
            }
            if (prev.length === 0) return [hour.start_utc];

            const hoursToday = day.hours;
            const idx = hoursToday.findIndex((h) => h.start_utc === hour.start_utc);
            const firstIdx = hoursToday.findIndex((h) => h.start_utc === prev[0]);
            const lastIdx = hoursToday.findIndex(
                (h) => h.start_utc === prev[prev.length - 1]
            );
            if (firstIdx === -1 || lastIdx === -1) return [hour.start_utc];

            if (idx === firstIdx - 1) return [hour.start_utc, ...prev];
            if (idx === lastIdx + 1) return [...prev, hour.start_utc];

            // Anything else: extend across the gap only if every hour between
            // is available, otherwise start again from here.
            const [lo, hi] = idx < firstIdx ? [idx, lastIdx] : [firstIdx, idx];
            const run = hoursToday.slice(lo, hi + 1);
            if (run.every((h) => h.status === "available")) {
                return run.map((h) => h.start_utc);
            }
            return [hour.start_utc];
        });
    }

    const quote = useMemo(() => {
        if (!data || !data.daily_rate || selected.length === 0) return null;
        const cents = Math.round((data.daily_rate * 100 / 24) * selected.length);
        return { hours: selected.length, cents };
    }, [data, selected]);

    useEffect(() => {
        if (!onChange) return;
        if (selected.length === 0) {
            onChange(null);
            return;
        }
        const sorted = [...selected].sort();
        const last = sorted[sorted.length - 1];
        const end = new Date(last + "Z");
        end.setUTCHours(end.getUTCHours() + 1);
        onChange({
            // Half-open: the end is the instant after the last hour bought, so
            // 9am-5pm occupies nine through four and releases five.
            start_time: sorted[0],
            end_time: end.toISOString().replace(/\.\d+Z$/, ""),
            hours: sorted.length,
            estimated_cents: quote ? quote.cents : null,
        });
    }, [selected, quote, onChange]);

    if (error) return <p className="hp__error">{error}</p>;
    if (!data) return <p className="hp__loading">Loading availability…</p>;

    if (!data.bookable) {
        return (
            <p className="hp__error">
                This screen has no rate set yet, so it cannot be booked.
            </p>
        );
    }

    return (
        <div className="hp">
            <div className="hp__head">
                <div>
                    <strong className="hp__title">Choose your hours</strong>
                    <p className="hp__sub">
                        Times are the screen’s local time ({data.timezone}).{" "}
                        {data.schedule_summary}.
                    </p>
                </div>
                <span className="hp__rate">{data.hourly_rate_display} / hour</span>
            </div>

            <div className="hp__days" role="tablist" aria-label="Day">
                {data.days.map((d, i) => (
                    <button
                        key={d.date}
                        type="button"
                        role="tab"
                        aria-selected={i === dayIndex}
                        className={"hp__day" + (i === dayIndex ? " is-active" : "")}
                        onClick={() => { setDayIndex(i); setSelected([]); }}
                    >
                        {new Date(d.date + "T12:00:00").toLocaleDateString(undefined, {
                            weekday: "short", month: "short", day: "numeric",
                        })}
                    </button>
                ))}
            </div>

            <ul className="hp__grid">
                {day.hours.map((h) => {
                    const picked = selected.includes(h.start_utc);
                    return (
                        <li key={h.start_utc}>
                            <button
                                type="button"
                                className={
                                    "hp__hour hp__hour--" + h.status +
                                    (picked ? " is-picked" : "")
                                }
                                disabled={h.status !== "available"}
                                onClick={() => toggle(h)}
                                title={STATUS_LABEL[h.status] || h.status}
                                aria-pressed={picked}
                            >
                                {h.label}
                            </button>
                        </li>
                    );
                })}
            </ul>

            <div className="hp__legend">
                <span><i className="hp__swatch hp__swatch--available" /> Available</span>
                <span><i className="hp__swatch hp__swatch--booked" /> Booked</span>
                <span><i className="hp__swatch hp__swatch--closed" /> Off air</span>
            </div>

            <div className="hp__quote">
                {quote ? (
                    <>
                        <strong>
                            {quote.hours} hour{quote.hours === 1 ? "" : "s"} ·{" "}
                            {money(quote.cents)}
                        </strong>
                        <button
                            type="button"
                            className="wb-btn wb-btn--ghost wb-btn--sm"
                            onClick={() => setSelected([])}
                        >
                            Clear
                        </button>
                    </>
                ) : (
                    <span className="hp__empty">
                        Pick an hour to start. Click another to extend the block.
                    </span>
                )}
            </div>
        </div>
    );
}
